/**
 * สถานะคิวคำสั่งออฟไลน์ของเครื่องสแกน
 *
 * อ่านจากคิว IndexedDB จริงใน `src/lib/offline/` — ไม่มีตัวเลขจำลองอีกแล้ว
 * (M11, `docs/TASKS.md`) และเป็นแหล่งเดียวของจำนวน "รอซิงค์" ที่หน้าจอแสดง
 *
 * สองเรื่องที่หน้าจอต้องไม่พูดผิด:
 *   - คิวที่อ่านไม่ได้ **ไม่ใช่** คิวว่าง จึงใช้ `count: null` ไม่ใช่ 0
 *   - คำสั่งที่เข้าคิวแล้วยัง **ไม่** ถือว่าบันทึกหรือโพสต์สำเร็จ ต้องขึ้นว่า
 *     รอซิงค์เสมอจนกว่า server จะตอบรับ (`docs/SECURITY.md` Offline/Online Policy)
 */

import { useCallback, useEffect, useState } from 'react';

import type { OfflineQueueErrorReason } from '../lib/offline/commandEnvelope';
import { readOfflineQueueStatus, subscribeToOfflineQueue } from '../lib/offline/commandQueue';

export type PendingSyncAvailability = 'CHECKING' | 'READY' | 'UNAVAILABLE';

export interface PendingSyncStatus {
  readonly availability: PendingSyncAvailability;
  /** จำนวนคำสั่งที่รอซิงค์ (`PENDING_LOCAL` + `SYNCING`) — `null` แปลว่ายังไม่รู้ ไม่ใช่ศูนย์ */
  readonly count: number | null;
  /** จำนวนที่เซิร์ฟเวอร์รับแล้ว — คำตอบจริงจากเซิร์ฟเวอร์ ไม่ใช่ผลของการกดส่ง */
  readonly accepted: number | null;
  /**
   * จำนวนที่ต้องมีคนมาดู (`CONFLICT` + `REJECTED`)
   *
   * ต้องอยู่บนหน้าจอหน้างานแม้จะแก้ที่นี่ไม่ได้ เพราะคนที่รู้ว่าคำสั่งใบนั้นคืออะไร
   * คือผู้ปฏิบัติงานที่เพิ่งสแกน — การซ่อนไว้จนกว่าจะมีใครเปิดหน้าจอ conflict บน
   * Desktop คือการปล่อยให้งานหายไปทั้งกะ (D-078)
   */
  readonly needsAttention: number | null;
  readonly reason: OfflineQueueErrorReason | null;
}

/** สถานะก่อนอ่านคิวรอบแรกเสร็จ — หน้าจอต้องเรนเดอร์ได้ทันทีโดยไม่รอ IndexedDB */
export const PENDING_SYNC_CHECKING: PendingSyncStatus = {
  availability: 'CHECKING',
  count: null,
  accepted: null,
  needsAttention: null,
  reason: null,
};

/** คำอธิบายสาเหตุที่คิวใช้งานไม่ได้ แบบที่ผู้ปฏิบัติงานหน้างานอ่านรู้เรื่อง */
export function describePendingSyncReason(reason: OfflineQueueErrorReason | null): string {
  switch (reason) {
    case 'INDEXEDDB_UNSUPPORTED':
      return 'เบราว์เซอร์นี้ไม่เปิดให้เก็บข้อมูลในเครื่อง';
    case 'UPGRADE_BLOCKED':
      return 'มีแท็บอื่นเปิดคิวรุ่นเก่าค้างอยู่ — ปิดแท็บนั้นแล้วเปิดใหม่';
    case 'QUOTA_EXCEEDED':
      return 'พื้นที่เก็บข้อมูลในเครื่องเต็ม';
    case 'OPEN_FAILED':
    case 'READ_FAILED':
    case 'WRITE_FAILED':
      return 'เปิดคิวในเครื่องไม่ได้';
    case 'NO_SECURE_RANDOM':
      return 'เครื่องนี้สร้างรหัสอ้างอิงคำสั่งไม่ได้';
    default:
      return 'คิวในเครื่องใช้งานไม่ได้';
  }
}

/**
 * ติดตามจำนวนคำสั่งที่รอซิงค์
 *
 * อ่านใหม่ทุกครั้งที่มีคำสั่งเข้าคิว เพื่อให้ตัวเลขบนหัวจอตรงกับของจริงเสมอ
 */
export function usePendingSyncStatus(): PendingSyncStatus {
  const [status, setStatus] = useState<PendingSyncStatus>(PENDING_SYNC_CHECKING);

  const refresh = useCallback(async (apply: (next: PendingSyncStatus) => void) => {
    const next = await readOfflineQueueStatus();
    apply(next);
  }, []);

  useEffect(() => {
    let active = true;
    const apply = (next: PendingSyncStatus) => {
      if (active) {
        setStatus(next);
      }
    };

    void refresh(apply);
    const unsubscribe = subscribeToOfflineQueue(() => {
      void refresh(apply);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [refresh]);

  return status;
}
